import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Database, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { NotificationCard } from "./shared/NotificationCard";
import {
  NOTIFICATION_TYPES,
  isNotificationEnabled,
  setNotificationEnabled,
  isSessionDismissed,
  dismissForSession,
  isPermanentlyDismissedForDate,
  dismissPermanentlyForDate,
} from "../lib/notificationUtils";
import apiClient from "../services/api";
const NOTIFICATION_TYPE = NOTIFICATION_TYPES.BACKUP_REMINDER;

// Days threshold for showing backup warning
const BACKUP_WARNING_DAYS = 5;

/**
 * Backup Alert Component
 * Shows warning if no backup has been completed in the last 5 days
 * Visible to Admin, Project Manager, Office Manager (NOT Foreman)
 */
export const BackupAlert = ({ userRole, getAuthHeaders }) => {
  const [backupInfo, setBackupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionDismissed, setSessionDismissed] = useState(() => isSessionDismissed(NOTIFICATION_TYPE));
  const [permanentlyDismissed, setPermanentlyDismissed] = useState(() => isPermanentlyDismissedForDate(NOTIFICATION_TYPE));

  // Don't show for Foremen
  const allowedRoles = ["admin", "project_manager", "office_manager"];
  const isAllowedRole = allowedRoles.includes(userRole?.toLowerCase());
  const notificationsEnabled = isNotificationEnabled(NOTIFICATION_TYPE);

  useEffect(() => {
    const fetchBackupStatus = async () => {
      if (!isAllowedRole || !notificationsEnabled) {
        setLoading(false);
        return;
      }

      try {
        const response = await apiClient.get(`/backup/last-backup`);
        setBackupInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch backup status:", error);
        setBackupInfo({ has_backup: false, days_since_backup: null });
      } finally {
        setLoading(false);
      }
    };

    fetchBackupStatus();
  }, [isAllowedRole, notificationsEnabled, getAuthHeaders]);

  // Handle session dismiss (dismiss until next login)
  const handleSessionDismiss = () => {
    dismissForSession(NOTIFICATION_TYPE);
    setSessionDismissed(true);
  };

  // Handle permanent dismiss for today
  const handlePermanentDismiss = () => {
    dismissPermanentlyForDate(NOTIFICATION_TYPE);
    setPermanentlyDismissed(true);
  };

  // Don't render conditions
  if (
    !isAllowedRole ||
    !notificationsEnabled ||
    sessionDismissed ||
    permanentlyDismissed ||
    loading
  ) {
    return null;
  }

  // Check if we should show the alert
  const shouldShowAlert = !backupInfo?.has_backup || 
    (backupInfo?.days_since_backup !== null && backupInfo.days_since_backup >= BACKUP_WARNING_DAYS);

  if (!shouldShowAlert) {
    return null;
  }

  const getMessage = () => {
    if (!backupInfo?.has_backup) {
      return "No backup has ever been created. Please create a backup to protect your data.";
    }
    return `It has been ${backupInfo.days_since_backup} days since the last backup. Regular backups help protect your data.`;
  };

  return (
    <NotificationCard
      variant="error"
      icon={<Database className="w-5 h-5" />}
      title={
        <span className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Backup Reminder
        </span>
      }
      onDismissSession={handleSessionDismiss}
      onDismissPermanent={handlePermanentDismiss}
      permanentDismissLabel="Dismiss for Today"
      actions={
        userRole?.toLowerCase() === "admin" && (
          <Link to="/backup">
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="create-backup-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Create Backup Now
            </Button>
          </Link>
        )
      }
      footer={
        backupInfo?.last_backup_date
          ? `Last backup: ${new Date(backupInfo.last_backup_date).toLocaleDateString()} by ${backupInfo.last_backup_by}`
          : null
      }
      testId="backup-alert"
    >
      <p className="text-sm text-red-700 dark:text-red-300">
        {getMessage()}
      </p>
    </NotificationCard>
  );
};

// Export utility functions for Settings page
export const getBackupNotificationsEnabled = () => isNotificationEnabled(NOTIFICATION_TYPE);
export const setBackupNotificationsEnabled = (enabled) => setNotificationEnabled(NOTIFICATION_TYPE, enabled);
